/**
 * Undo Label Utilities
 *
 * Provides standardized label generation for undo/redo menu items.
 * Labels follow the format: "<Object> [<Part>] <Verb>"
 * Examples: "Node Creation", "Link Target Change", "5-Element Selection Copy"
 */
import type {TranslateService} from '@ngx-translate/core';

// Object types for undo labels
export type UndoObjectType = 'Smap' | 'Node' | 'Link' | 'Selection' | 'Clipboard' | 'Text' | 'Overlay';

// Verb types - base verbs for operations
export type UndoVerb = 'Creation' | 'Change' | 'Deletion' | 'Cut' | 'Copy' | 'Paste' | 'Addition' | 'Removal';

/**
 * Metadata structure for generating labels
 */
export interface UndoLabelMetadata {
  object: UndoObjectType;
  verb: UndoVerb;
  part?: string;           // Name, Description, Property, Style, Comment, State, Container, Position, etc.
  elementCount?: number;   // For Selection, optionally show "5-Element Selection"
}

/**
 * Generate a human-readable label from metadata
 * @param metadata The label metadata
 * @returns A label like "Node Creation", "Link Target Change", "5-Element Selection Copy"
 */
export function generateUndoLabel(metadata: UndoLabelMetadata): string {
  const {object, verb, part, elementCount} = metadata;

  // Build the object part
  let objectStr: string;
  if (object === 'Selection' && elementCount !== undefined && elementCount > 1) {
    objectStr = `${elementCount}-Element Selection`;
  } else {
    objectStr = object;
  }

  // Build the full label
  if (part) {
    return `${objectStr} ${part} ${verb}`;
  }

  return `${objectStr} ${verb}`;
}

// i18n key mappings for the closed object/verb enums.
const UNDO_OBJECT_KEYS: Record<UndoObjectType, string> = {
  Smap: 'UNDO_REDO.OBJECT.SMAP',
  Node: 'UNDO_REDO.OBJECT.NODE',
  Link: 'UNDO_REDO.OBJECT.LINK',
  Selection: 'UNDO_REDO.OBJECT.SELECTION',
  Clipboard: 'UNDO_REDO.OBJECT.CLIPBOARD',
  Text: 'UNDO_REDO.OBJECT.TEXT',
  Overlay: 'UNDO_REDO.OBJECT.OVERLAY'
};

const UNDO_VERB_KEYS: Record<UndoVerb, string> = {
  Creation: 'UNDO_REDO.VERB.CREATION',
  Change: 'UNDO_REDO.VERB.CHANGE',
  Deletion: 'UNDO_REDO.VERB.DELETION',
  Cut: 'UNDO_REDO.VERB.CUT',
  Copy: 'UNDO_REDO.VERB.COPY',
  Paste: 'UNDO_REDO.VERB.PASTE',
  Addition: 'UNDO_REDO.VERB.ADDITION',
  Removal: 'UNDO_REDO.VERB.REMOVAL'
};

// Maps an English style display name (as produced by the *_STYLE_NAMES tables and
// getStyleDisplayName) to its i18n key, so a style-derived label `part` can be
// resolved at render time. Any `part` value not present here (command-provided
// literals such as 'Comment'/'Position', or the capitalizeFirstLetter fallback)
// is rendered verbatim, exactly as before.
const STYLE_DISPLAY_TO_KEY: Record<string, string> = {
  'Color': 'UNDO_REDO.STYLE.COLOR',
  'Color Intensity': 'UNDO_REDO.STYLE.COLOR_INTENSITY',
  'Background Image Size': 'UNDO_REDO.STYLE.BACKGROUND_IMAGE_SIZE',
  'Color Opacity': 'UNDO_REDO.STYLE.COLOR_OPACITY',
  'Image': 'UNDO_REDO.STYLE.IMAGE',
  'Image Opacity': 'UNDO_REDO.STYLE.IMAGE_OPACITY',
  'Image Scale': 'UNDO_REDO.STYLE.IMAGE_SCALE',
  'Appearance': 'UNDO_REDO.STYLE.APPEARANCE',
  'Sound': 'UNDO_REDO.STYLE.SOUND',
  'Sound Volume': 'UNDO_REDO.STYLE.SOUND_VOLUME',
  'Sound Effect': 'UNDO_REDO.STYLE.SOUND_EFFECT',
  'Layout': 'UNDO_REDO.STYLE.LAYOUT',
  'Shape': 'UNDO_REDO.STYLE.SHAPE',
  'Frame Thickness': 'UNDO_REDO.STYLE.FRAME_THICKNESS',
  'Frame Stroke': 'UNDO_REDO.STYLE.FRAME_STROKE',
  'Stroke': 'UNDO_REDO.STYLE.STROKE',
  'Thickness': 'UNDO_REDO.STYLE.THICKNESS',
  'Size': 'UNDO_REDO.STYLE.SIZE',
  'Directionality': 'UNDO_REDO.STYLE.DIRECTIONALITY',
  'Head Style': 'UNDO_REDO.STYLE.HEAD_STYLE',
  'Inclusion': 'UNDO_REDO.STYLE.INCLUSION',
  'Label Size': 'UNDO_REDO.STYLE.LABEL_SIZE',
  'Label Value': 'UNDO_REDO.STYLE.LABEL_VALUE',
  'Label Color': 'UNDO_REDO.STYLE.LABEL_COLOR',
  'Label Rotation': 'UNDO_REDO.STYLE.LABEL_ROTATION',
  'Label Slope': 'UNDO_REDO.STYLE.LABEL_SLOPE',
  'Label Alignment': 'UNDO_REDO.STYLE.LABEL_ALIGNMENT',
  'Label Distance': 'UNDO_REDO.STYLE.LABEL_DISTANCE',
  'Label Horizontal Position': 'UNDO_REDO.STYLE.LABEL_HORIZONTAL_POSITION',
  'Label Vertical Position': 'UNDO_REDO.STYLE.LABEL_VERTICAL_POSITION',
  'Label Horizontal Offset': 'UNDO_REDO.STYLE.LABEL_HORIZONTAL_OFFSET',
  'Label Vertical Offset': 'UNDO_REDO.STYLE.LABEL_VERTICAL_OFFSET'
};

/**
 * Localized counterpart of generateUndoLabel. Rebuilds the exact same
 * "<Object> [<Part>] <Verb>" label from metadata, but resolves each atom via
 * i18n keys and composes through interpolated template keys. Rendered English is
 * identical to generateUndoLabel; the composition, spacing, and any verbatim
 * (command-provided) part are preserved.
 */
export function translateUndoLabel(
  metadata: UndoLabelMetadata,
  translate: TranslateService
): string {
  const {object, verb, part, elementCount} = metadata;

  // Object segment (mirrors generateUndoLabel's objectStr branch)
  let objectStr: string;
  if (object === 'Selection' && elementCount !== undefined && elementCount > 1) {
    objectStr = translate.instant('UNDO_REDO.SELECTION_COUNT', {count: elementCount});
  } else {
    const objectKey = UNDO_OBJECT_KEYS[object];
    objectStr = objectKey ? translate.instant(objectKey) : object;
  }

  // Verb segment
  const verbKey = UNDO_VERB_KEYS[verb];
  const verbStr = verbKey ? translate.instant(verbKey) : verb;

  // Part segment: translate known style-derived names; pass everything else through.
  if (part) {
    const styleKey = STYLE_DISPLAY_TO_KEY[part];
    const partStr = styleKey ? translate.instant(styleKey) : part;
    return translate.instant('UNDO_REDO.LABEL_WITH_PART', {
      object: objectStr,
      part: partStr,
      verb: verbStr
    });
  }

  return translate.instant('UNDO_REDO.LABEL', {object: objectStr, verb: verbStr});
}

/**
 * Determine object type based on element counts
 * @param nodeCount Number of nodes in the operation
 * @param linkCount Number of links in the operation
 * @param isTextField Whether this is a text field operation
 * @returns The appropriate UndoObjectType
 */
export function determineObjectType(
  nodeCount: number,
  linkCount: number,
  isTextField?: boolean
): UndoObjectType {
  if (isTextField) return 'Text';
  if (nodeCount === 1 && linkCount === 0) return 'Node';
  if (nodeCount === 0 && linkCount === 1) return 'Link';
  if (nodeCount + linkCount >= 2) return 'Selection';
  return 'Smap'; // Empty selection = smap
}

// Style name mappings for readable labels

export const SMAP_STYLE_NAMES: Record<string, string> = {
  'color': 'Color',
  'colorIntensity': 'Color Intensity',
  'backgroundImageUrl': 'Image',
  // MAP-1613: without an entry here the key fell through to capitalizeFirstLetter
  // and surfaced raw as "BackgroundImageSize", untranslatable.
  'backgroundImageSize': 'Background Image Size',
  'imageUrl': 'Image',
  'opacity': 'Image Opacity',
  'imageOpacity': 'Image Opacity',
  'appearance': 'Appearance',
  'backgroundSoundUrl': 'Sound',
  'backgroundSoundVolume': 'Sound Volume',
  'automaticLayout': 'Layout'
};

export const NODE_STYLE_NAMES: Record<string, string> = {
  'color': 'Color',
  'type': 'Shape',    // Node shape is stored as 'type'
  'shape': 'Shape',
  'weight': 'Frame Thickness',  // weight is the actual field name for border thickness
  'borderWidth': 'Frame Thickness',
  'frameThickness': 'Frame Thickness',
  'borderStyle': 'Frame Stroke',
  'frameStroke': 'Frame Stroke',
  'image': 'Image',
  'imageUrl': 'Image',
  'soundEffect': 'Sound Effect',
  'properties.scale': 'Size',
  'size': 'Size',
  'fontSize': 'Label Size',
  'labelSize': 'Label Size',
  'labelSource': 'Label Value',
  'colorIntensity': 'Color Intensity',
  'opacity': 'Color Opacity',
  'colorOpacity': 'Color Opacity',
  'imageOpacity': 'Image Opacity',
  'imageScale': 'Image Scale',
  'labelHorizontalPosition': 'Label Horizontal Position',
  'labelVerticalPosition': 'Label Vertical Position',
  'labelHorizontalOffset': 'Label Horizontal Offset',
  'labelVerticalOffset': 'Label Vertical Offset',
  'excludedFromState': 'Inclusion',
  // Properties nested under properties.*
  'properties.soundUrl': 'Sound',
  'properties.textFontSize': 'Label Size',
  'properties.textFontColor': 'Label Color',
  'properties.label': 'Label Value',
  'properties.opacity': 'Color Intensity',
  'properties.colorOpacity': 'Color Opacity',
  'properties.imageOpacity': 'Image Opacity',
  'properties.imageScale': 'Image Scale',
  'properties.textHalign': 'Label Horizontal Position',
  'properties.textValign': 'Label Vertical Position',
  'properties.textMarginX': 'Label Horizontal Offset',
  'properties.textMarginY': 'Label Vertical Offset',
  'properties.textRotation': 'Label Rotation',
  'properties.borderStyle': 'Frame Stroke'
};

export const LINK_STYLE_NAMES: Record<string, string> = {
  'color': 'Color',
  'type': 'Shape',
  'shape': 'Stroke',  // In link forms, 'shape' field controls stroke style (solid/dashed/dotted)
  'width': 'Thickness',
  'thickness': 'Thickness',
  'weight': 'Thickness',
  'lineStyle': 'Stroke',
  'stroke': 'Stroke',
  'direction': 'Directionality',    // Form field name for directionality
  'directionality': 'Directionality',
  'edgeArrowType': 'Head Style',
  'soundEffect': 'Sound Effect',
  'soundUrl': 'Sound',
  'fontSize': 'Label Size',
  'textFontSize': 'Label Size',
  'labelSize': 'Label Size',
  'label': 'Label Value',
  'labelSource': 'Label Value',
  'targetArrowShape': 'Head Style',
  'headStyle': 'Head Style',
  'labelSlope': 'Label Slope',
  'labelAlignment': 'Label Alignment',
  'labelDistance': 'Label Distance',
  'linkLabelOffset': 'Label Distance',
  'linkLabelPosition': 'Label Alignment',
  'excludedFromState': 'Inclusion'
};

/**
 * Get a readable style name from a property key
 * @param propertyKey The property key (e.g., 'color', 'type', 'properties.scale')
 * @param elementType The element type ('node', 'link', or 'smap')
 * @returns A readable name like "Color", "Shape", "Size"
 */
export function getStyleDisplayName(
  propertyKey: string,
  elementType: 'node' | 'link' | 'smap'
): string {
  const mappings = elementType === 'smap'
    ? SMAP_STYLE_NAMES
    : elementType === 'node'
      ? NODE_STYLE_NAMES
      : LINK_STYLE_NAMES;

  return mappings[propertyKey] || capitalizeFirstLetter(propertyKey);
}

/**
 * Capitalize the first letter of a string
 */
function capitalizeFirstLetter(str: string): string {
  if (!str) return str;
  // Handle nested properties like "properties.scale" -> "Scale"
  const lastPart = str.includes('.') ? str.split('.').pop()! : str;
  return lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
}

// Priority order for style properties - higher priority properties are shown first in labels
const NODE_STYLE_PRIORITY = [
  'type',           // Shape is most visually distinctive
  'color',
  'imageUrl',
  'properties.scale',
  'properties.opacity',
  'properties.colorOpacity',
  'properties.imageOpacity',
  'properties.imageScale',
  'properties.textFontSize',
  'properties.textFontColor',
  'properties.label',
  'properties.borderStyle',
  'properties.textValign',
  'properties.textHalign',
  'properties.textMarginX',
  'properties.textMarginY',
  'properties.textRotation',
  'properties.soundUrl',
  'weight',
  'excludedFromState'
];

const LINK_STYLE_PRIORITY = [
  'type',           // Shape is most visually distinctive
  'color',
  'width',
  'lineStyle',
  'direction',      // Form field name for directionality
  'directionality',
  'targetArrowShape',
  'textFontSize',
  'label',
  'labelSource',
  'labelSlope',
  'linkLabelPosition',
  'linkLabelOffset',
  'soundUrl',
  'excludedFromState'
];

const SMAP_STYLE_PRIORITY = [
  'appearance',
  'color',
  'colorIntensity',
  'backgroundImageUrl',
  'imageUrl',
  'opacity',
  'imageOpacity',
  'backgroundSoundUrl',
  'backgroundSoundVolume',
  'automaticLayout'
];

/**
 * Get the primary style being changed from a styles object
 * Returns the most important changed style's display name based on priority
 */
export function getPrimaryStyleName(
  styles: Record<string, unknown>,
  elementType: 'node' | 'link' | 'smap'
): string | undefined {
  const keys = Object.keys(styles);
  if (keys.length === 0) return undefined;

  // Filter out internal/technical properties
  const displayableKeys = keys.filter(k =>
    !k.startsWith('cyedge') &&
    !k.includes('Positions') &&
    k !== 'id'
  );

  if (displayableKeys.length === 0) return undefined;

  // Get priority list based on element type
  const priorityList = elementType === 'smap'
    ? SMAP_STYLE_PRIORITY
    : elementType === 'node'
      ? NODE_STYLE_PRIORITY
      : LINK_STYLE_PRIORITY;

  // Find the highest priority key that was changed
  for (const priorityKey of priorityList) {
    if (displayableKeys.includes(priorityKey)) {
      return getStyleDisplayName(priorityKey, elementType);
    }
  }

  // Fall back to first displayable key if none match priority list
  return getStyleDisplayName(displayableKeys[0], elementType);
}
